import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Stack,
  InputAdornment,
  TextField,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Search as SearchIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import api from '../../services/api';
import { toast } from 'react-toastify';
import ClienteDialog from '../../components/Clientes/ClienteDialog';
import { format } from 'date-fns';
import ptBR from 'date-fns/locale/pt-BR';

const defaultCliente = {
  nome: '',
  cpf: '',
  rg: '',
  data_nascimento: null,
  telefone: '',
  email: '',
  cep: '',
  logradouro: '',
  numero: '',
  complemento: '',
  bairro: '',
  cidade: '',
  estado: '',
  empresa: '',
  telefone_comercial: '',
  cargo: '',
  renda_mensal: '',
  referencia_name1: '',
  referencia_telefone1: '',
  referencia_name2: '',
  referencia_telefone2: '',
  referencia_name3: '',
  referencia_telefone3: ''
};

export default function Clientes() {
  const [clientes, setClientes] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [selectedCliente, setSelectedCliente] = useState(null);
  const [filtro, setFiltro] = useState('');
  const [clientesFiltrados, setClientesFiltrados] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, cliente: null });

  useEffect(() => {
    loadClientes();
  }, []);

  const loadClientes = async (searchTerm = '') => {
    try {
      setLoading(true);
      console.log('Buscando clientes com termo:', searchTerm);
      
      const params = {};
      if (searchTerm && searchTerm.trim() !== '') {
        params.search = searchTerm.trim();
      }
      
      const response = await api.get('/clientes', { params });
      
      console.log('Resposta da API:', response.data);
      
      setClientes(response.data);
      setClientesFiltrados(response.data);
    } catch (error) {
      console.error('Erro ao carregar clientes:', error);
      toast.error('Erro ao carregar clientes. Verifique o console para mais detalhes.');
    } finally {
      setLoading(false);
    }
  };

  // Debounce para evitar muitas chamadas à API
  useEffect(() => {
    const timer = setTimeout(() => {
      loadClientes(filtro);
    }, 500); // 500ms de atraso

    return () => clearTimeout(timer);
  }, [filtro]);

  const handleOpenModal = (cliente = null) => {
    setSelectedCliente(cliente || defaultCliente);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setSelectedCliente(null);
    setOpenModal(false);
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      if (selectedCliente.id) {
        await api.put(`/clientes/${selectedCliente.id}`, selectedCliente);
        toast.success('Cliente atualizado com sucesso!');
      } else {
        await api.post('/clientes', selectedCliente);
        toast.success('Cliente cadastrado com sucesso!');
      }
      handleCloseModal();
      loadClientes();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao salvar cliente');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      setLoading(true);
      await api.delete(`/clientes/${deleteDialog.cliente.id}`);
      toast.success('Cliente excluído com sucesso!');
      setDeleteDialog({ open: false, cliente: null });
      loadClientes();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao excluir cliente');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return '';
    try {
      return format(new Date(date), 'dd/MM/yyyy', { locale: ptBR });
    } catch (error) {
      return '';
    }
  };

  return (
    <Box sx={{ p: { xs: 1, md: 2 }, pl: { xs: 1, md: 1.5 } }}>
      <Stack 
        direction={{ xs: 'column', sm: 'row' }} 
        justifyContent="space-between" 
        alignItems={{ xs: 'stretch', sm: 'center' }} 
        gap={1}
        mb={3}
      >
        <Typography variant="h5" component="h1">
          Clientes
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => handleOpenModal()}
          sx={{ width: { xs: '100%', sm: 'auto' } }}
        >
          Novo Cliente
        </Button>
      </Stack>

      <Paper sx={{ mb: 2, p: 2 }}>
        <TextField
          fullWidth
          placeholder="Buscar por nome ou CPF..."
          value={filtro}
          onChange={(e) => setFiltro(e.target.value)}
          disabled={loading}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Paper>

      {/* Visualização em Tabela (Desktop) */}
      <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Nome</TableCell>
                <TableCell>CPF</TableCell>
                <TableCell>Telefone</TableCell>
                <TableCell>Email</TableCell>
                <TableCell align="center">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : clientesFiltrados.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    Nenhum cliente encontrado
                  </TableCell>
                </TableRow>
              ) : (
                clientesFiltrados.map((cliente) => (
                  <TableRow key={cliente.id}>
                    <TableCell>{cliente.nome}</TableCell>
                    <TableCell>{cliente.cpf}</TableCell>
                    <TableCell>{cliente.telefone}</TableCell>
                    <TableCell>{cliente.email}</TableCell>
                    <TableCell align="center">
                      <IconButton
                        color="primary"
                        onClick={() => handleOpenModal(cliente)}
                        size="small"
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        color="error"
                        onClick={() => setDeleteDialog({ open: true, cliente })}
                        size="small"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
      
      {/* Visualização em Cards (Mobile) */}
      <Box sx={{ display: { xs: 'block', sm: 'none' } }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
            <CircularProgress />
          </Box>
        ) : clientesFiltrados.length === 0 ? (
          <Paper sx={{ p: 3, textAlign: 'center' }}>
            <Typography>Nenhum cliente encontrado</Typography>
          </Paper>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {clientesFiltrados.map((cliente) => (
              <Paper 
                key={cliente.id}
                sx={{ 
                  p: 2, 
                  borderRadius: 1,
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }}
              >
                <Box sx={{ mb: 1 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                    {cliente.nome}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    CPF: {cliente.cpf}
                  </Typography>
                </Box>
                
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: 1.5 }}>
                  <Typography variant="body2">
                    <strong>Telefone:</strong> {cliente.telefone || '-'}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Email:</strong> {cliente.email || '-'}
                  </Typography>
                </Box>
                
                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                  <Button
                    variant="outlined"
                    color="primary"
                    size="small"
                    startIcon={<EditIcon />}
                    onClick={() => handleOpenModal(cliente)}
                  >
                    Editar
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    size="small"
                    startIcon={<DeleteIcon />}
                    onClick={() => setDeleteDialog({ open: true, cliente })}
                  >
                    Excluir
                  </Button>
                </Box>
              </Paper>
            ))}
          </Box>
        )}
      </Box>

      <ClienteDialog
        open={openModal}
        handleClose={handleCloseModal}
        handleSave={handleSave}
        cliente={selectedCliente}
        setCliente={setSelectedCliente}
        fullScreen={window.innerWidth <= 600}
      />

      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, cliente: null })}
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <WarningIcon color="warning" />
            <Typography>Confirmar Exclusão</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography>
            Tem certeza que deseja excluir o cliente {deleteDialog.cliente?.nome}?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteDialog({ open: false, cliente: null })}
            color="inherit"
          >
            Cancelar
          </Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Excluir
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}